import React, { FC, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useButton, useCurrency, useI18n, useSelector } from '~Hooks';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '~Assets/Css/custom-calendar-styles.css';
import navigateLeftIcon from '~/Assets/icons//navigate-left.svg';
import navigateRightIcon from '~/Assets/icons//navigate-right.svg';
import calendarIcon from '~Assets/icons/calendar_month.svg';
import closeIcon from '~Assets/icons/close.svg';
import historyIcon from '~Assets/icons/history.svg';
import { DateTime } from 'luxon';
import Heading from '~ComponentsImproved/Typography/Heading';
import styled from '@emotion/styled';
import { Box, GrayButton } from '~Styles';
import { useStorage } from '~Components/Storage';
import { ModalContent } from '~Styles/modal';
import { DarkCalendar } from '~Styles/calendar';
import Icon from '~ComponentsImproved/Icon';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import AddRemoveIcon from '../AddRemoveIcon';
import { GetCashEvents, GetCashEventsVariables } from './__queries__';

const CASH_EVENTS_QUERY = gql`
  query GetCashEvents($deviceId: ID!, $sentAfter: DateTime!, $sentBefore: DateTime) {
    POSDevice(id: $deviceId) {
      id
      cashEvents {
        events(sentAfter: $sentAfter, sentBefore: $sentBefore) {
          lastKey
          items
        }
      }
    }
  }
`;

const Wrapper = styled(ModalContent)`
  background-color: var(--colorBlueGray1200);
  padding: 0;
  max-width: 600px;
  max-height: calc(100% - 64px);
`;

// Title row with a hairline divider separating it from the controls below.
const Header = styled(Box)`
  padding: 16px;
  border-bottom: 1px solid var(--colorBlueGray1100);
`;

// Let the title take the remaining space and stay on one line; the close
// button keeps its full 52px and is never clipped.
const Title = styled.div`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Body = styled(Box)`
  padding: 16px 16px 24px;
  overflow-y: auto;
`;

// Square icon button reused for close, prev day, and next day.
const IconButton = styled(GrayButton)<{ disabled?: boolean }>`
  width: 42px;
  height: 42px;
  min-width: 42px;
  min-height: 42px;
  padding: 0;
  border-radius: 10px;
  background: var(--colorBlueGray1100);
  &:active {
    background: var(--colorBlueGray1000);
  }
  ${({ disabled }) => disabled && 'opacity: 0.3; pointer-events: none;'}
`;

// Tapping toggles the calendar; gold outline marks the open state.

const DateButton = styled(GrayButton)<{ active?: boolean }>`
  flex: 1;
  min-width: 0;
  width: 52px;
  height: 52px;
  min-height: 52px;
  gap: 8px;
  padding: 0 12px;
  border-radius: 10px;
  text-transform: none;
  white-space: nowrap;
  background: var(--colorBlueGray1100);
  border: 2px solid ${({ active }) => (active ? 'var(--colorUIYellow)' : 'transparent')};
  &:active {
    background: var(--colorBlueGray1000);
  }
  & > * {
    white-space: nowrap;
  }
`;

const CalendarCard = styled(DarkCalendar)`
  margin-top: 12px;
  animation: expand 0.18s ease-out;

  .react-calendar {
    background: var(--colorBlueGray1300);
    border-radius: 12px;
    padding: 16px;
  }

  @keyframes expand {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SectionLabel = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  font-size: 13px;
  color: var(--colorBlueGray800);
  margin: 24px 0 16px;
`;

const EmptyIconCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--colorBlueGray1100);
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface Props {
  cashSources: { id: string; name: string }[];
  onClose?: () => void;
}

const PayoutHistory: FC<Props> = ({ cashSources, onClose }) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();

  const language = useSelector((state) => state.language);
  const companyStaff = useSelector((state) => state.companyStaff);

  let lang: string = language;
  if (language == 'se') lang = 'sv';
  const currency = useCurrency();

  const [chosenDate, setChosenDate] = useState<Date>(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);

  const dateLabel = DateTime.fromJSDate(chosenDate).toFormat('yyyy-MM-dd');

  const eventsQuery = useQuery<GetCashEvents, GetCashEventsVariables>(CASH_EVENTS_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      deviceId: storage.taxComplianceDeviceId || '',
      sentAfter: DateTime.fromJSDate(chosenDate).startOf('day'),
      sentBefore: DateTime.fromJSDate(chosenDate)
        .startOf('day')
        .plus({ hours: 24 })
        .toISO(),
    },
    skip: !storage.taxComplianceDeviceId,
  });

  const filteredEvents = (eventsQuery.data?.POSDevice?.cashEvents?.events?.items || []).filter(
    (event: any) => event.type === 'CASH_CHANGE',
  );

  const closeButton = useButton({ onClick: () => onClose?.() });

  const toggleCalendarButton = useButton({
    onClick: () => setCalendarOpen((open) => !open),
  });

  const prevButton = useButton({
    onClick: () => {
      const prev = DateTime.fromJSDate(chosenDate).minus({ days: 1 });
      setChosenDate(prev.toJSDate());
    },
  });

  const nextButton = useButton({
    onClick: () => {
      const next = DateTime.fromJSDate(chosenDate).plus({ days: 1 });
      setChosenDate(next.toJSDate());
    },
    disabled: DateTime.fromJSDate(chosenDate).hasSame(DateTime.fromJSDate(new Date()), 'day'),
  });

  return (
    <Wrapper display="flex" flexDirection="column">
      <Header fluid display="flex" alignItems="center" justifyContent="space-between" gap="small">
        <Title>
          <Heading level={3} color="var(--colorWhite)">
            {i18n('CashManagement.PayoutHistory')}
          </Heading>
        </Title>
        <IconButton {...closeButton}>
          <Icon src={closeIcon} color="var(--colorBlueGray100)" width="24px" height="24px" />
        </IconButton>
      </Header>

      <Body fluid display="flex" flexDirection="column">
        <Box fluid display="flex" alignItems="center" gap="xs">
          <IconButton {...prevButton}>
            <Icon
              src={navigateLeftIcon}
              color="var(--colorBlueGray200)"
              width="24px"
              height="24px"
            />
          </IconButton>

          <DateButton active={calendarOpen} {...toggleCalendarButton}>
            <Icon src={calendarIcon} color="var(--colorWhite)" width="22px" height="22px" />
            <Heading level={4} color="var(--colorWhite)">
              {dateLabel}
            </Heading>
          </DateButton>

          <IconButton {...nextButton}>
            <Icon
              src={navigateRightIcon}
              color="var(--colorBlueGray200)"
              width="24px"
              height="24px"
            />
          </IconButton>
        </Box>

        {calendarOpen && (
          <CalendarCard>
            <Calendar
              onChange={(date: any) => {
                setChosenDate(date);
                setCalendarOpen(false);
              }}
              value={chosenDate}
              locale={lang}
              maxDate={new Date()}
            />
          </CalendarCard>
        )}

        <SectionLabel>
          {i18n('CashManagement.Payouts')} · {dateLabel}
        </SectionLabel>

        {eventsQuery.loading ? (
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            gap="small"
            justifyContent="center"
            marginTop="large">
            <Loader color="var(--colorWhite)" />
            <Paragraph level={3} color="var(--colorWhite)">
              {i18n('General.LoadingDots')}
            </Paragraph>
          </Box>
        ) : filteredEvents.length > 0 ? (
          <Box display="flex" flexDirection="column" gap="small" fluid>
            {[...filteredEvents].reverse().map((event: any, index: number) => {
              const foundStaffName = companyStaff?.staffs.find(
                (staff) => staff.id === event.staffId,
              )?.name;

              return (
                <Box
                  key={event.id || `${event.createdAt}-${index}`}
                  fluid
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap="small">
                  <Box display="flex" alignItems="center" gap="xs">
                    <AddRemoveIcon amount={event.amount} />

                    <Box display="flex" flexDirection="column">
                      <Heading color="var(--colorWhite)" level={4}>
                        {i18n(
                          event.amount > 0
                            ? 'CashManagement.PayoutHistory.Deposited'
                            : 'CashManagement.PayoutHistory.From',
                        )}{' '}
                        {event.sourceId === 'DRAWER'
                          ? i18n('CashManagement.CashRegister')
                          : cashSources.find((source) => source.id === event.sourceId)?.name}
                      </Heading>
                      <Paragraph color="var(--colorBlueGray200)" level={5}>
                        {DateTime.fromISO(event.createdAt).toFormat('HH:mm:ss')}
                        {foundStaffName && ` - ${foundStaffName}`}
                      </Paragraph>
                    </Box>
                  </Box>
                  <Heading
                    level={4}
                    color={event.amount > 0 ? 'var(--colorUIGreen)' : 'var(--colorUIRed)'}>
                    {currency.format(event.amount)}
                  </Heading>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            gap="medium"
            justifyContent="center"
            marginTop="large"
            marginBottom="large">
            <EmptyIconCircle>
              <Icon src={historyIcon} color="var(--colorBlueGray800)" width="32px" height="32px" />
            </EmptyIconCircle>
            <Paragraph level={3} color="var(--colorBlueGray500)" style={{ textAlign: 'center' }}>
              {i18n('CashManagement.PayoutHistory.NoPayouts')}
            </Paragraph>
          </Box>
        )}
      </Body>
    </Wrapper>
  );
};

export default React.memo(PayoutHistory);
